import { Calendar, Clock, Mail, MoreVertical, CheckCircle2, XCircle, Circle } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';

const EVENT_TYPE_LABELS = {
  deadline: 'Deadline',
  appointment: 'Appointment',
  sale_end: 'Sale End',
  reminder: 'Reminder'
};

const EVENT_TYPE_COLORS = {
  deadline: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  appointment: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  sale_end: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
  reminder: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200'
};

const STATUS_ICONS = {
  pending: Circle,
  completed: CheckCircle2,
  cancelled: XCircle
};

const STATUS_COLORS = {
  pending: 'text-blue-600 dark:text-blue-400',
  completed: 'text-green-600 dark:text-green-400',
  cancelled: 'text-gray-400 dark:text-gray-600'
};

export default function EventList({ events = [], onEventClick }) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Calendar className="h-16 w-16 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium mb-2">No events found</h3>
        <p className="text-sm text-muted-foreground">
          Create an event or adjust your filters
        </p>
      </div>
    );
  }

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.event_date).toDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedEvents).sort((a, b) => {
    return new Date(a) - new Date(b);
  });

  return (
    <div className="overflow-y-auto h-full">
      <div className="p-6 space-y-6">
        {sortedDates.map(dateStr => {
          const date = new Date(dateStr);
          const isToday = date.toDateString() === new Date().toDateString();
          const isPast = date < new Date() && !isToday;
          const dayEvents = groupedEvents[dateStr];

          return (
            <div key={dateStr}>
              {/* Date Header */}
              <div className={`flex items-center gap-3 mb-3 pb-2 border-b ${
                isToday ? 'border-blue-500' : ''
              }`}>
                <Calendar className={`h-4 w-4 ${
                  isToday ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'
                }`} />
                <h3 className={`font-medium ${
                  isToday ? 'text-blue-600 dark:text-blue-400' : ''
                }`}>
                  {formatDate(date)}
                  {isToday && <span className="ml-2 text-xs">(Today)</span>}
                  {isPast && <span className="ml-2 text-xs text-muted-foreground">(Past)</span>}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
                </span>
              </div>

              {/* Events for this date */}
              <div className="space-y-2">
                {dayEvents.map(event => {
                  const StatusIcon = STATUS_ICONS[event.status] || Circle;
                  const isUpcoming = date > new Date() && event.status === 'pending';
                  const isOverdue = isPast && event.status === 'pending';

                  return (
                    <div
                      key={event.id}
                      onClick={() => onEventClick?.(event.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
                        isUpcoming ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800' :
                        isOverdue ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800' :
                        'bg-background'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <StatusIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${STATUS_COLORS[event.status]}`} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {event.event_type && (
                              <Badge className={`${EVENT_TYPE_COLORS[event.event_type] || ''} text-xs`}>
                                {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                              </Badge>
                            )}
                            {isUpcoming && (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 text-xs">
                                Upcoming
                              </Badge>
                            )}
                            {isOverdue && (
                              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200 text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>

                          <h4 className="font-medium mb-1">{event.title}</h4>

                          {event.description && (
                            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                              {event.description}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(event.event_date).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            {event.email_count > 0 && (
                              <div className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {event.email_count} {event.email_count === 1 ? 'email' : 'emails'}
                              </div>
                            )}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Could open a context menu here
                          }}
                          className="h-8 w-8 p-0 flex-shrink-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
